import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  getGetKnowledgeDocumentsQueryKey,
  useCreateKnowledgeDocument,
  useUpdateKnowledgeDocument,
} from "~/api/default/default";
import { UpdateKnowledgeDocumentBodyStatus } from "~/api/model";
import { uploadFileToS3 } from "~/lib/s3";
import logger from "~/utils/logger";

interface UseKnowledgeDocumentUploadParams {
  organizationId: string;
}

export const useKnowledgeDocumentUpload = ({
  organizationId,
}: UseKnowledgeDocumentUploadParams) => {
  const { mutateAsync: createKnowledgeDocument } = useCreateKnowledgeDocument();
  const { mutateAsync: updateKnowledgeDocument } = useUpdateKnowledgeDocument();
  const queryClient = useQueryClient();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadSingleFile = async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    // Create knowledge document and get presigned URL
    const { presignedUrl, documentId } = await createKnowledgeDocument({
      organizationId,
      data: {
        filename: file.name,
        contentType: file.type,
        size: file.size,
        ext: file.name.includes(".") ? (file.name.split(".").pop() ?? "") : "",
      },
    });

    // Refetch documents list to show uploading state
    await queryClient.invalidateQueries({
      queryKey: getGetKnowledgeDocumentsQueryKey(organizationId),
    });

    // Upload file to S3 with progress tracking
    await uploadFileToS3(presignedUrl, file, onProgress);

    // Update document status to completed
    await updateKnowledgeDocument({
      organizationId,
      documentId,
      data: {
        status: UpdateKnowledgeDocumentBodyStatus.COMPLETED,
      },
    });

    // Refetch documents list
    await queryClient.invalidateQueries({
      queryKey: getGetKnowledgeDocumentsQueryKey(organizationId),
    });
  };

  const uploadKnowledgeDocument = async (file: File): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      setUploadProgress(10);

      await uploadSingleFile(file, (progress) => {
        const progressPct = 20 + Math.round(progress * 0.7);
        setUploadProgress(progressPct);
      });

      setUploadProgress(100);
    } catch (error) {
      logger.error(`Error uploading knowledge document ${file.name}:`, error);
      throw error;
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const uploadKnowledgeDocuments = async (files: File[]): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const baseProgress = (i / files.length) * 100;
        const fileProgressRange = 100 / files.length;

        await uploadSingleFile(file, (progress) => {
          const progressPct = Math.round(
            baseProgress + progress * fileProgressRange * 0.9
          );
          setUploadProgress(progressPct);
        });

        // Update progress after each file completes
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
    } catch (error) {
      logger.error("Error uploading knowledge documents:", error);
      throw error;
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  return {
    uploadKnowledgeDocument,
    uploadKnowledgeDocuments,
    isUploading,
    uploadProgress,
  };
};
